import { inject, Injectable } from '@angular/core';
import {
  BdGrantsService,
  revokeGrantConfirmMessage,
  ShareableGrantFull,
} from '@bindoc-projects/bindoc-permissions-lib/data';
import {
  BdSharePermission,
  BdSharePermissionHelper,
  ShareableLabel,
} from '@bindoc-projects/resource-domain-lib';
import {
  BdEditLabelsDialogComponent,
  EditLabelsDialogData,
  EditLabelsDialogResult,
} from '../../edit-labels-dialog/edit-labels-dialog.component';
import { BdShareableSelectService } from '../../select/global-search/shareable-select.service';
import { BD_DANGER_MENU_ITEM_STYLE } from '../../info-menu/danger-menu-item';
import { DIALOG_CONFIG_FOR_FILTERS } from '@bindoc-projects/util-lib/ui';
import { BdConfirmDeleteService } from '@bindoc-projects/ui-lib/dialog/ui';
import { MenuItem } from 'primeng/api';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { take } from 'rxjs';

import { ItemSelectionData, SelectedItem } from '../dialog.models';
import { BdGrantDialogComponent } from '../grant-dialog/grant-dialog.component';
import { BdPermissionsWithDetailsDialogComponent } from '../permission-detail-dialog/permissions-with-details-dialog.component';
import { FolderItem } from '../shareables-overview.models';

/**
 * What a host has to supply so the shared menu can act and report back. Every
 * callback is the host's own state update: the global "Geteilt" list patches
 * its in-memory grants, the group tab drops the cached index and reloads.
 */
export interface GrantActionContext {
  /** The grant behind the row — revoking needs its grantee. */
  readonly grant?: ShareableGrantFull;
  /** `false` renders no menu at all (picker embeddings). */
  readonly showActions: boolean;
  readonly onOpen: (item: FolderItem) => void;
  readonly onLabelsChanged: (
    item: FolderItem,
    labels: ShareableLabel[],
  ) => void;
  readonly onRevoked: (item: FolderItem) => void;
}

/**
 * The row actions of a *granted* resource — everything the user can do with
 * something that was shared with them rather than owned by them.
 *
 * Lives in a service because two lists show the same rows: the global "Geteilt"
 * tab and the per-group shared tab inside the folder tree. They differ only in
 * how they refresh afterwards, which is what {@link GrantActionContext}
 * carries.
 *
 * Provided per component, not in root: the confirm dialog it opens resolves the
 * `ConfirmationService` the host provides, so the confirmation lands in the
 * host's own `<p-confirmdialog>`.
 */
@Injectable()
export class BdGrantActionsService {
  private readonly _grantsService = inject(BdGrantsService);
  private readonly _dialogService = inject(DialogService);
  private readonly _confirmDeleteService = inject(BdConfirmDeleteService);
  private readonly _selectService = inject(BdShareableSelectService);

  public menuItems(item: FolderItem, ctx: GrantActionContext): MenuItem[] {
    if (!ctx.showActions) {
      return [];
    }
    const allowed = item.allowedPermissions ?? [];
    const highest =
      allowed.length > 0
        ? BdSharePermissionHelper.getHighestPermission(allowed)
        : null;
    const canUpdate =
      highest !== null && BdSharePermissionHelper.includes(highest, 'UPDATE');
    // Re-sharing opens the grant dialog, whose share-preview step the backend
    // gates on DELETE — gate the entry on DELETE so it never opens into a
    // 403 dead end (BDA-5507).
    const canShare =
      highest !== null && BdSharePermissionHelper.includes(highest, 'DELETE');
    return [
      {
        label: 'Öffnen',
        icon: 'pi pi-external-link',
        command: () => ctx.onOpen(item),
      },
      {
        label: 'In neuem Tab öffnen',
        icon: 'pi pi-window-maximize',
        command: () => this._selectService.openInNewTab(item.id, item.type),
      },
      {
        label: 'Labels bearbeiten',
        icon: 'pi pi-tag',
        visible: canUpdate,
        command: () => this.editLabels(item, ctx),
      },
      {
        label: 'Erneut freigeben',
        icon: 'pi pi-share-alt',
        visible: canShare,
        command: () => this.regrant(item, highest),
      },
      {
        label: 'Zugriff',
        icon: 'pi pi-key',
        visible: canUpdate,
        command: () => this.showPermissions(item),
      },
      {
        label: 'Freigabe entfernen',
        icon: 'pi pi-trash',
        ...BD_DANGER_MENU_ITEM_STYLE,
        command: () => this.revoke(item, ctx),
      },
    ];
  }

  public showPermissions(item: FolderItem): void {
    const dialogConfig: DynamicDialogConfig = {
      ...DIALOG_CONFIG_FOR_FILTERS,
      header: 'Zugriff',
      data: {
        shareableType: item.type,
        shareableId: item.id,
        shareableName: item.name,
      },
    };
    this._dialogService.open(
      BdPermissionsWithDetailsDialogComponent,
      dialogConfig,
    );
  }

  public editLabels(item: FolderItem, ctx: GrantActionContext): void {
    const dialogConfig: DynamicDialogConfig<EditLabelsDialogData> = {
      ...DIALOG_CONFIG_FOR_FILTERS,
      header: `Labels für „${item.name}“ bearbeiten`,
      data: {
        shareableType: item.type,
        shareableId: item.id,
        shareableName: item.name,
      },
    };
    this._dialogService
      .open(BdEditLabelsDialogComponent, dialogConfig)
      .onClose.pipe(take(1))
      .subscribe((result: EditLabelsDialogResult | null) => {
        if (result) {
          ctx.onLabelsChanged(item, result.labels);
        }
      });
  }

  public regrant(
    item: FolderItem,
    initialPermission: BdSharePermission | null,
  ): void {
    const dialogConfig: DynamicDialogConfig<ItemSelectionData> = {
      ...DIALOG_CONFIG_FOR_FILTERS,
      header: 'Erneut freigeben',
      data: {
        items: [toSelectedItem(item)],
        initialPermission: initialPermission ?? undefined,
      },
    };
    this._dialogService
      .open(BdGrantDialogComponent, dialogConfig)
      .onClose.pipe(take(1))
      .subscribe();
  }

  public revoke(item: FolderItem, ctx: GrantActionContext): void {
    const grant = ctx.grant;
    if (!grant) {
      return;
    }
    this._confirmDeleteService
      .confirm({
        customHeader: 'Freigabe entfernen?',
        customMessage: revokeGrantConfirmMessage(item.name, grant.grantee),
        customAcceptLabel: 'Ja, Freigabe entfernen',
      })
      .subscribe(() => {
        this._grantsService
          .revoke(grant.shareableReference, grant.grantee)
          .pipe(take(1))
          .subscribe(() => ctx.onRevoked(item));
      });
  }
}

/** A row as the share/move dialogs expect it. */
export function toSelectedItem(item: FolderItem): SelectedItem {
  return {
    id: item.id,
    type: item.type,
    displayName: item.name,
    typeLabel: item.typeLabel,
    modifiedDate: item.modifiedDate,
  };
}
